import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { FaArrowRight, FaChartLine, FaShieldAlt, FaBolt } from "react-icons/fa";

const HeroSection = () => {
    const { isAuthenticated } = useContext(AuthContext);
    
    return (
        <section className="relative min-h-[700px] flex items-center overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800">
                <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:40px_40px]"></div>
                
                {/* Animated circles */}
                <div className="absolute top-20 left-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute top-40 right-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-20 left-40 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>
            
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
                <div className="text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-8">
                        <FaBolt className="text-yellow-300 mr-2" />
                        <span className="text-white text-sm font-medium">AI-Powered Risk Intelligence Platform</span>
                    </div>
                    
                    {/* Main Title */}
                    <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                        Risk Assessment
                        <span className="block bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
                            Reimagined
                        </span>
                    </h1>
                    
                    {/* Description */}
                    <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
                        Transform complex risk data into actionable insights in seconds. 
                        Powered by advanced AI for European businesses.
                    </p>
                    
                    {/* CTA Button */}
                    <div className="flex flex-col items-center mb-16">
                        <Link to={isAuthenticated ? "/products" : "/auth/signup"}>
                            <button className="group bg-white text-blue-700 px-10 py-5 rounded-full font-semibold text-lg hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center">
                                {isAuthenticated ? "Start Risk Assessment" : "Start Free Trial"}
                                <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
                            </button>
                        </Link>
                        <p className="text-blue-100 text-sm mt-4">Free credits included • No credit card required</p>
                    </div>
                    
                    {/* Feature Cards */}
                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                            <FaChartLine className="text-3xl text-yellow-300 mb-3" />
                            <h3 className="text-white font-semibold mb-2">Real-time Analysis</h3>
                            <p className="text-blue-200 text-sm">Get instant risk assessments with live data processing</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                            <FaShieldAlt className="text-3xl text-green-300 mb-3" />
                            <h3 className="text-white font-semibold mb-2">99.9% Accuracy</h3>
                            <p className="text-blue-200 text-sm">Industry-leading precision in risk predictions</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
                            <FaBolt className="text-3xl text-purple-300 mb-3" />
                            <h3 className="text-white font-semibold mb-2">Lightning Fast</h3>
                            <p className="text-blue-200 text-sm">Complete assessments in under 3 seconds</p>
                        </div>
                    </div>
                    
                    {/* Trust Line */}
                    <div className="mt-16 text-center">
                        <p className="text-blue-200 text-sm">Trusted by 10,000+ businesses across Europe</p>
                    </div>
                </div>
            </div>
            
            {/* Add CSS for blob animation */}
            <style jsx>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                
                .animate-blob {
                    animation: blob 7s infinite;
                }
                
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </section>
    );
};

export default HeroSection;